/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {ReactElement, ReactNode} from 'react';
import Button, {ButtonProps} from '@material-ui/core/Button';
import {makeStyles} from '@material-ui/core/styles';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {DLSTheme} from '../../../types/types';
import {StableButtonLabel} from './StableButtonLabel';

export interface PrimaryButtonProps extends ButtonProps {
  children: ReactNode;
  icon?: ReactElement;
};

const iconStyles = {
  '& .MuiButton-startIcon' : {
    color       : 'inherit',
    marginLeft  : '0 !important',
    marginRight : '0 !important',
    '& svg'     : {
      color : 'inherit'
    },
    '& svg [fill]:not([fill="none"])' : {
      fill : 'currentColor'
    },
    '& svg [stroke]:not([stroke="none"])' : {
      stroke : 'currentColor'
    }
  }
};

const useStyles = makeStyles((theme: DLSTheme) => ({
  container : {
    padding : '3px'
  },
  root : {
    gap             : '4px',
    display         : 'flex',
    minHeight       : '32px',
    padding         : '0 8px',
    color           : dlsColor('white'),
    height          : '32px',
    fontSize        : '14px !important',
    fontFamily      : 'Inter',
    fontWeight      : 500,
    lineHeight      : '20px',
    borderRadius    : '8px !important',
    letterSpacing   : '0px !important',
    textTransform   : 'capitalize',
    backgroundColor : dlsColor('darkTeal'),
    ...iconStyles,
    '&:hover'       : {
      backgroundColor : dlsColor('aaBlue'),
      boxShadow       : 'none',
      color           : dlsColor('white'),
      padding         : '0px 8px'
    },
    '&:active' : {
      color           : dlsColor('darkTeal'),
      backgroundColor : dlsColor('paleBlue'),
      boxShadow       : 'none',
      padding         : '0px 8px'
    },
    '&[aria-pressed="true"]' : {
      color           : dlsColor('darkTeal'),
      backgroundColor : dlsColor('paleBlue'),
      boxShadow       : 'none',
      padding         : '0px 8px'
    },
    '&:focus' : {
      outline : 'none'
    },
    '&:focus-visible' : {
      filter        : 'none',
      outline       : `2px solid ${dlsColor('skyBlue')}`,
      borderRadius  : '6px',
      outlineOffset : '2px'
    },
    '&$disabled, &:disabled' : {
      color           : dlsColor('grey4'),
      backgroundColor : dlsColor('grey8')
    }
  },
  disabled : {}
}));

/**
 * Primary buttons are used to convey the most urgent or main actions on a particular
 * page. In cards, use the regular primary buttons. If paired, use it with the
 * tertiary button.
 *
 * ```typescript
 *
 * import React from 'react';
 * import {PrimaryButton} from '@ion/dls/src/components/buttons/';
 *
 * function MyComponent {
 *   return (
 *     <React.Fragment>
 *       <PrimaryButton>Default</PrimaryButton>
 *       <br />
 *       <PrimaryButton disabled>Disabled</PrimaryButton>
 *       <br />
 *       <PrimaryButton fullWidth>Full Width</PrimaryButton>
 *     </React.Fragment>
 *   );
 * }
 *
 * ```
 *
 */
export const PrimaryButton = ({
  children,
  icon,
  startIcon,
  ...props
}: PrimaryButtonProps) => {
  const classes = useStyles();

  return (
    <div className={classes.container}>
      <Button variant='contained'
        tabIndex={props.disabled ? -1 : 0}
        classes={{
          root     : classes.root,
          disabled : classes.disabled
        }}
        disableRipple
        disableFocusRipple
        color='primary'
        startIcon={icon || startIcon}
        {...props}>
        <StableButtonLabel>{children}</StableButtonLabel>
      </Button>
    </div>
  );
};

export default PrimaryButton;
